import { Authenticated, Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";

import { FreshBuild } from "@/components/FreshBuild";
import { AppLayout } from "@/components/layout/AppLayout";
import { ScreenFallback } from "@/components/layout/ScreenFallback";
import { authProvider, dataProvider } from "@/lib/providers";
import { loginPath } from "@/lib/loginReturn";
import { LoginPage } from "@/pages/LoginPage";
import { PlansPage } from "@/pages/PlansPage";

/*
 * ЭКРАНЫ — СВОИМИ ЧАНКАМИ ПО ПЕРВОМУ ПЕРЕХОДУ (волна F спеки
 * 2026-10-12-code-audit-refactor). До неё 46 экранов ехали одним файлом на 1,5 МБ
 * на вход в кабинет — и перекачивались целиком после КАЖДОЙ сборки, а сборок в
 * день несколько. Статически остаются форма входа и «Планы»: это первый кадр
 * кабинета (`/` ведёт на `/plans`), и лишний круг за чанком ему ни к чему.
 *
 * Страницы экспортируют именованный `XPage`, а `lazy()` ждёт `default` — отсюда
 * `.then`. Вкладка со СТАРОЙ сборкой при переходе попросит чанк, которого на
 * диске уже нет, — это ловит `vite:preloadError` в `main.tsx`.
 */
const DemandPage = lazy(() => import("@/pages/DemandPage").then((m) => ({ default: m.DemandPage })));
const BqdCardPage = lazy(() => import("@/pages/BqdCardPage").then((m) => ({ default: m.BqdCardPage })));
const BqdStartPage = lazy(() => import("@/pages/BqdStartPage").then((m) => ({ default: m.BqdStartPage })));
const OrderCardPage = lazy(() => import("@/pages/OrderCardPage").then((m) => ({ default: m.OrderCardPage })));
const OrdersPage = lazy(() => import("@/pages/OrdersPage").then((m) => ({ default: m.OrdersPage })));
const PurchaseDashPage = lazy(() => import("@/pages/PurchaseDashPage").then((m) => ({ default: m.PurchaseDashPage })));
const ProductPage = lazy(() => import("@/pages/ProductPage").then((m) => ({ default: m.ProductPage })));
const ProductStartPage = lazy(() => import("@/pages/ProductStartPage").then((m) => ({ default: m.ProductStartPage })));
const ProductsPage = lazy(() => import("@/pages/ProductsPage").then((m) => ({ default: m.ProductsPage })));
const SeasonPage = lazy(() => import("@/pages/SeasonPage").then((m) => ({ default: m.SeasonPage })));
const ReportPage = lazy(() => import("@/pages/ReportPage").then((m) => ({ default: m.ReportPage })));
const RivalsPage = lazy(() => import("@/pages/RivalsPage").then((m) => ({ default: m.RivalsPage })));
const ContentQueuePage = lazy(() =>
  import("@/pages/ContentQueuePage").then((m) => ({ default: m.ContentQueuePage })),
);
const ContentControlPage = lazy(() =>
  import("@/pages/ContentControlPage").then((m) => ({ default: m.ContentControlPage })),
);
const StatPage = lazy(() => import("@/pages/StatPage").then((m) => ({ default: m.StatPage })));
const SupplierCardPage = lazy(() => import("@/pages/SupplierCardPage").then((m) => ({ default: m.SupplierCardPage })));
const SuppliersPage = lazy(() => import("@/pages/SuppliersPage").then((m) => ({ default: m.SuppliersPage })));
const BalancePage = lazy(() => import("@/pages/BalancePage").then((m) => ({ default: m.BalancePage })));
const ContentChangePage = lazy(() => import("@/pages/ContentChangePage").then((m) => ({ default: m.ContentChangePage })));
const ContentChangesPage = lazy(() => import("@/pages/ContentChangesPage").then((m) => ({ default: m.ContentChangesPage })));
const PhotoHistoryPage = lazy(() => import("@/pages/PhotoHistoryPage").then((m) => ({ default: m.PhotoHistoryPage })));
const ProfitPage = lazy(() => import("@/pages/ProfitPage").then((m) => ({ default: m.ProfitPage })));
const PricePage = lazy(() => import("@/pages/PricePage").then((m) => ({ default: m.PricePage })));
const PriceSetPage = lazy(() => import("@/pages/PriceSetPage").then((m) => ({ default: m.PriceSetPage })));
const PriceComparePage = lazy(() =>
  import("@/pages/PriceComparePage").then((m) => ({ default: m.PriceComparePage })),
);
const PriceControlPage = lazy(() =>
  import("@/pages/PriceControlPage").then((m) => ({ default: m.PriceControlPage })),
);
const PaymentsPage = lazy(() => import("@/pages/PaymentsPage").then((m) => ({ default: m.PaymentsPage })));
const PenaltiesPage = lazy(() => import("@/pages/PenaltiesPage").then((m) => ({ default: m.PenaltiesPage })));
const PhotoshootPage = lazy(() => import("@/pages/PhotoshootPage").then((m) => ({ default: m.PhotoshootPage })));
const PriemkaDayPage = lazy(() => import("@/pages/PriemkaDayPage").then((m) => ({ default: m.PriemkaDayPage })));
const PriemkaPage = lazy(() => import("@/pages/PriemkaPage").then((m) => ({ default: m.PriemkaPage })));
const StockPage = lazy(() => import("@/pages/StockPage").then((m) => ({ default: m.StockPage })));
const PermissionsPage = lazy(() => import("@/pages/PermissionsPage").then((m) => ({ default: m.PermissionsPage })));
const SeoPage = lazy(() => import("@/pages/SeoPage").then((m) => ({ default: m.SeoPage })));
const AnalysisPage = lazy(() => import("@/pages/AnalysisPage").then((m) => ({ default: m.AnalysisPage })));
const SyncPage = lazy(() => import("@/pages/SyncPage").then((m) => ({ default: m.SyncPage })));
const TagsPage = lazy(() => import("@/pages/TagsPage").then((m) => ({ default: m.TagsPage })));
const DimensionsPage = lazy(() => import("@/pages/DimensionsPage").then((m) => ({ default: m.DimensionsPage })));
const PromoPage = lazy(() => import("@/pages/PromoPage").then((m) => ({ default: m.PromoPage })));
const PromoProductsPage = lazy(() => import("@/pages/PromoProductsPage").then((m) => ({ default: m.PromoProductsPage })));
const PromoOffersPage = lazy(() => import("@/pages/PromoOffersPage").then((m) => ({ default: m.PromoOffersPage })));
const PromoAdvisorPage = lazy(() => import("@/pages/PromoAdvisorPage").then((m) => ({ default: m.PromoAdvisorPage })));
const PromoDecisionsPage = lazy(() => import("@/pages/PromoDecisionsPage").then((m) => ({ default: m.PromoDecisionsPage })));
const CargoListPage = lazy(() => import("@/pages/CargoListPage").then((m) => ({ default: m.CargoListPage })));
const SupplyOzonCardPage = lazy(() => import("@/pages/SupplyOzonCardPage").then((m) => ({ default: m.SupplyOzonCardPage })));
const SupplyOzonPage = lazy(() => import("@/pages/SupplyOzonPage").then((m) => ({ default: m.SupplyOzonPage })));
const SupplyWbCardPage = lazy(() => import("@/pages/SupplyWbCardPage").then((m) => ({ default: m.SupplyWbCardPage })));
const SupplyCalcPage = lazy(() => import("@/pages/SupplyCalcPage").then((m) => ({ default: m.SupplyCalcPage })));
const ControlPage = lazy(() => import("@/pages/ControlPage").then((m) => ({ default: m.ControlPage })));
const FbsControlPage = lazy(() => import("@/pages/FbsControlPage").then((m) => ({ default: m.FbsControlPage })));
const FbsStatPage = lazy(() => import("@/pages/FbsStatPage").then((m) => ({ default: m.FbsStatPage })));
const ReturnsPage = lazy(() => import("@/pages/ReturnsPage").then((m) => ({ default: m.ReturnsPage })));
const ReturnsDayPage = lazy(() => import("@/pages/ReturnsDayPage").then((m) => ({ default: m.ReturnsDayPage })));
const ReturnCardPage = lazy(() => import("@/pages/ReturnCardPage").then((m) => ({ default: m.ReturnCardPage })));
const SupplyPodsortPage = lazy(() => import("@/pages/SupplyPodsortPage").then((m) => ({ default: m.SupplyPodsortPage })));
const SupplySlotsPage = lazy(() => import("@/pages/SupplySlotsPage").then((m) => ({ default: m.SupplySlotsPage })));
const SupplyOzonSlotsPage = lazy(() =>
  import("@/pages/SupplyOzonSlotsPage").then((m) => ({ default: m.SupplyOzonSlotsPage })),
);
const SupplyWbPage = lazy(() => import("@/pages/SupplyWbPage").then((m) => ({ default: m.SupplyWbPage })));

export function App() {
  return (
    <BrowserRouter>
      <FreshBuild />
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        resources={[
          { name: "plans", list: "/plans", meta: { label: "Планы" } },
          { name: "season", list: "/season", meta: { label: "Сезонность" } },
          { name: "demand", list: "/demand", meta: { label: "Спрос" } },
          { name: "stat", list: "/stat", meta: { label: "Статистика" } },
        ]}
        options={{ disableTelemetry: true, warnWhenUnsavedChanges: true }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              /* Ключ `fallback` ведёт на форму входа: незалогиненный оператор
                 не должен видеть каркас кабинета даже мгновение. */
              <Authenticated
                key="app"
                fallback={<Navigate to={loginPath()} replace />}
              >
                <AppLayout>
                  {/* Граница ожидания ленивого экрана — ВНУТРИ каркаса: меню и
                      шапка стоят, пока чанк едет. Переходы внутри кабинета сюда
                      обычно не доходят: react-router меняет адрес в
                      `startTransition`, и React держит прежний экран до
                      прихода нового; заставка — на холодный вход по ссылке. */}
                  <Suspense fallback={<ScreenFallback />}>
                    <Outlet />
                  </Suspense>
                </AppLayout>
              </Authenticated>
            }
          >
            <Route index element={<Navigate to="/plans" replace />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/dashboard/control" element={<ControlPage />} />
            <Route path="/dashboard/purchase" element={<PurchaseDashPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:orderId" element={<OrderCardPage />} />
            <Route path="/cargo" element={<CargoListPage />} />
            <Route path="/bqd" element={<BqdStartPage />} />
            <Route path="/bqd/:qrId" element={<BqdCardPage />} />
            <Route path="/season" element={<SeasonPage />} />
            <Route path="/demand" element={<DemandPage />} />
            <Route path="/stat" element={<StatPage />} />
            <Route path="/seo" element={<SeoPage />} />
            <Route path="/analysis" element={<AnalysisPage />} />
            <Route path="/analysis/:slug" element={<AnalysisPage />} />
            <Route path="/promo" element={<PromoPage />} />
            <Route path="/promo/products" element={<PromoProductsPage />} />
            <Route path="/promo/offers" element={<PromoOffersPage />} />
            <Route path="/promo/advisor" element={<PromoAdvisorPage />} />
            <Route path="/promo/decisions" element={<PromoDecisionsPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/priemka" element={<PriemkaPage />} />
            {/* День приёмщика — свой адрес, а не панель: его посылают самому
                сотруднику, и он открывает свой день прямой ссылкой. */}
            <Route path="/priemka/day/:date/:user" element={<PriemkaDayPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/tags" element={<TagsPage />} />
            <Route path="/dimensions" element={<DimensionsPage />} />
            <Route path="/content/changes" element={<ContentChangesPage />} />
            <Route path="/content/changes/:changeId" element={<ContentChangePage />} />
            <Route path="/content/rivals" element={<RivalsPage />} />
            <Route path="/content/photos" element={<PhotoHistoryPage />} />
            <Route path="/content/queue" element={<ContentQueuePage />} />
            <Route path="/content/control" element={<ContentControlPage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/dashboard/stock" element={<StockPage />} />
            <Route path="/dashboard/photoshoot" element={<PhotoshootPage />} />
            <Route path="/dashboard/plans" element={<PlanDashboardMoved />} />
            <Route path="/supply/podsort-korob" element={<SupplyCalcPage />} />
            <Route path="/supply/calc" element={<SupplyCalcMoved />} />
            <Route path="/supply/podsort" element={<SupplyPodsortPage />} />
            <Route path="/fbs" element={<Navigate to="/fbs/control" replace />} />
            <Route path="/fbs/control" element={<FbsControlPage />} />
            <Route path="/fbs/stat" element={<FbsStatPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/returns/:mp/list" element={<ReturnsDayPage />} />
            <Route path="/returns/:mp/r/:id" element={<ReturnCardPage />} />
            <Route path="/returns/:mp/:day" element={<ReturnsDayPage />} />
            <Route path="/supply/wb" element={<SupplyWbPage />} />
            <Route path="/supply/wb/slots" element={<SupplySlotsPage />} />
            <Route path="/supply/wb/:supplyId" element={<SupplyWbCardPage />} />
            <Route path="/supply/ozon" element={<SupplyOzonPage />} />
            <Route path="/supply/ozon/slots" element={<SupplyOzonSlotsPage />} />
            <Route path="/supply/ozon/:orderId" element={<SupplyOzonCardPage />} />
            <Route path="/finance/balance" element={<BalancePage />} />
            <Route path="/finance/profit" element={<ProfitPage />} />
            <Route path="/finance/penalties" element={<PenaltiesPage />} />
            <Route path="/finance/price" element={<PricePage />} />
            <Route path="/finance/price/set" element={<PriceSetPage />} />
            <Route path="/finance/price/compare" element={<PriceComparePage />} />
            <Route path="/finance/price/control" element={<PriceControlPage />} />
            <Route path="/finance/payments" element={<PaymentsPage />} />
            <Route path="/settings/permissions" element={<PermissionsPage />} />
            <Route path="/settings/sync" element={<SyncPage />} />
            {/* Старый адрес баланса ведёт на новый, а не отдаёт пустоту: экран
                жил в «Дашборде» первые сутки, и ссылка на него уже могла уйти
                человеку. Редирект стоит ровно столько, сколько строка. */}
            <Route
              path="/dashboard/balance"
              element={<Navigate to="/finance/balance" replace />}
            />
            {/* Карточку товара в `resources` Refine НЕ добавляем: пункта меню
                у неё нет — её открывают ОТ товара, а не ищут в списке. */}
            {/* Без номера — переход к первой карточке кольца поставщика
                (ссылки «Поставщиков»). */}
            <Route path="/product" element={<ProductStartPage />} />
            <Route path="/product/:productId" element={<ProductPage />} />
            {/* Карточка поставщика (владелец 25.09.26); его товары —
                параметром `/products?supplier=`, до них с карточки один клик. */}
            <Route path="/supplier/:supplierId" element={<SupplierCardPage />} />
          </Route>
        </Routes>
      </Refine>
    </BrowserRouter>
  );
}

/**
 * Дашборд плановых заказов переехал вкладкой в «Планы» (28.09.26): старые
 * закладки и пересланные ссылки ведут туда же, с тем же отбором в адресе.
 */
function PlanDashboardMoved() {
  const { search } = useLocation();
  const next = new URLSearchParams(search);
  next.set("tab", "dashboard");
  return <Navigate to={`/plans?${next}`} replace />;
}

/**
 * «Расчёт» стал «Подсортом короба» (21.10.26): подсорт бывает коробами и
 * штуками. Старые закладки `/supply/calc` ведут туда же, с тем же отбором.
 */
function SupplyCalcMoved() {
  const { search } = useLocation();
  return <Navigate to={`/supply/podsort-korob${search}`} replace />;
}
